/**
 * Bandz real-time data sync — WebSocket client.
 *
 * A single lazily-connected WebSocket to `ws(s)://<host>/ws`, authenticated by
 * the same `bandz.sid` session cookie as /api (same-origin, so the cookie rides
 * the handshake automatically). The server only ever sends band-updated events
 * for bands the session may access, and never echoes a client's own write back
 * to it — together that means: no cross-tenant leaks and no write-back loops.
 *
 * When a `band-updated` arrives we (a) invalidate that band's offline cache so
 * the next fetch is fresh, and (b) notify subscribers so data hooks can refetch
 * live. The connection auto-reconnects with capped exponential backoff.
 */
import { invalidateCache } from './offline';

export interface RealtimeEvent {
  type: 'band-updated';
  bandId: string;
}

export interface RealtimeState {
  connected: boolean;
}

let ws: WebSocket | null = null;
let manualDisconnect = false;
let reconnectDelayMs = 1000; // grows 1s → 2s → 4s … capped at 30s
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let connected = false;

const eventListeners = new Set<(ev: RealtimeEvent) => void>();
const stateListeners = new Set<(s: RealtimeState) => void>();

function emitEvent(ev: RealtimeEvent) {
  eventListeners.forEach((l) => {
    try { l(ev); } catch { /* subscriber errors must not break the socket */ }
  });
}

function emitState() {
  const s: RealtimeState = { connected };
  stateListeners.forEach((l) => {
    try { l(s); } catch { /* ignore */ }
  });
}

/**
 * Invalidate the offline cache entries that a band-updated for `bandId` could
 * have made stale: the band's own scoped endpoints plus the shared aggregates
 * (team / shows / setlists / checklists / calendar) that the UI filters by band.
 * Non-fatal — the cache is only read while offline, and repopulates on the next
 * successful GET.
 */
export async function invalidateBandCache(bandId: string): Promise<void> {
  const paths = [
    `/api/bands/${bandId}`,
    '/api/team',
    '/api/shows',
    '/api/setlists',
    '/api/checklists',
    '/api/calendar',
    `/api/itinerary/${bandId}`,
  ];
  await Promise.all(paths.map((p) => invalidateCache(p).catch(() => undefined)));
}

function handleMessage(raw: string) {
  let msg: unknown;
  try { msg = JSON.parse(raw); } catch { return; }
  if (!msg || typeof msg !== 'object') return;
  const m = msg as Record<string, unknown>;
  if (m.type === 'band-updated' && typeof m.bandId === 'string') {
    void invalidateBandCache(m.bandId);
    emitEvent({ type: 'band-updated', bandId: m.bandId });
  }
}

function scheduleReconnect() {
  if (manualDisconnect) return;
  if (reconnectTimer) return;
  const delay = reconnectDelayMs;
  reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

/** Open (or reopen) the WebSocket. Safe to call repeatedly. */
export function connect(): void {
  if (typeof window === 'undefined') return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  manualDisconnect = false;

  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${proto}//${window.location.host}/ws`;
  try {
    ws = new WebSocket(url);
  } catch {
    scheduleReconnect();
    return;
  }

  ws.onopen = () => {
    connected = true;
    reconnectDelayMs = 1000; // reset backoff on a successful connection
    emitState();
  };
  ws.onmessage = (ev) => {
    if (typeof ev.data === 'string') handleMessage(ev.data);
  };
  ws.onclose = () => {
    connected = false;
    ws = null;
    emitState();
    scheduleReconnect();
  };
  ws.onerror = () => {
    // onclose follows; nothing to do here beyond closing a broken socket.
    try { ws && ws.close(); } catch { /* ignore */ }
  };
}

/** Close the socket and stop reconnecting (logout / teardown). */
export function disconnect(): void {
  manualDisconnect = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (ws) {
    try { ws.onclose = null; ws.close(); } catch { /* ignore */ }
    ws = null;
  }
  connected = false;
  emitState();
}

/** Ensure the socket is connected (idempotent). */
export function ensureConnected(): void {
  if (typeof window === 'undefined') return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
  connect();
}

// getRealtimeState REMOVED (audit 2026-10-24 [DEAD]): subscribers use
// subscribeRealtimeState — nobody polled the snapshot getter.

/** Subscribe to band-updated events. Returns an unsubscribe function. */
export function subscribeBandUpdates(cb: (ev: RealtimeEvent) => void): () => void {
  eventListeners.add(cb);
  return () => eventListeners.delete(cb);
}

/** Subscribe to connection-state changes. Returns an unsubscribe function. */
export function subscribeRealtimeState(cb: (s: RealtimeState) => void): () => void {
  stateListeners.add(cb);
  cb({ connected });
  return () => stateListeners.delete(cb);
}
